import { type AltoIconName, type ColorVariant } from '@alto/design-library-tokens';
import React, { useEffect } from 'react';
import { calculateIconPxSize } from './utils';

type IconColorVariants = Record<ColorVariant, { readonly main: string; readonly duo: string }>;

/** The props a per-brand wrapper accepts; the mapping and colors are supplied by the wrapper. */
export type MaterialSymbolIconBrandProps = {
  readonly accessibilityLabel?: string;
  readonly allowFontScaling?: boolean;
  readonly name: AltoIconName;
  /**
   * Use for testing-library identification purposes, only if you can't rely on user-readable text or a11y labels.
   */
  readonly testID?: string;
  readonly type?: ColorVariant;
};

type Props = MaterialSymbolIconBrandProps & {
  readonly colorVariants: IconColorVariants;
  readonly iconMap: Record<string, string | undefined>;
};

const getMaterialIconName = (name: AltoIconName, iconMap: Props['iconMap']): string | undefined => {
  let baseIconName = name.split('-')[0];
  // There are variants for checkbox and radio unchecked and checked (duo) states.
  // Preserve checkbox-duo and radio-duo names for the checked state.
  if (name === 'checkbox-duo' || name === 'radio-duo') {
    baseIconName = name;
  }

  return iconMap[baseIconName];
};

/**
 * Renders an Alto icon name as a Material Symbol. Shared by every brand that uses Material Symbols;
 * each brand supplies its own mapping and color variants so the differences stay in the tokens.
 */
/**
 * `index.html` requests the Material Symbols stylesheet for every host, but if that request was
 * dropped the ligature would render as its literal text ("calendar_month", "arrow_forward_ios"…).
 *
 * Requesting it here covers both paths: this component only mounts for the brands that need the
 * font, and the id makes it a no-op when the document already asked for it.
 */
const FONT_STYLESHEET_ID = 'material-symbols-outlined-font';
const FONT_STYLESHEET_HREF =
  'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200&display=block';

const requestMaterialSymbolsStylesheet = () => {
  if (document.getElementById(FONT_STYLESHEET_ID)) {
    return;
  }

  const preconnect = document.createElement('link');
  preconnect.rel = 'preconnect';
  preconnect.href = 'https://fonts.gstatic.com';
  preconnect.crossOrigin = 'anonymous';
  document.head.appendChild(preconnect);

  const stylesheet = document.createElement('link');
  stylesheet.id = FONT_STYLESHEET_ID;
  stylesheet.rel = 'stylesheet';
  stylesheet.href = FONT_STYLESHEET_HREF;
  document.head.appendChild(stylesheet);
};

export const MaterialSymbolIcon = ({
  accessibilityLabel,
  allowFontScaling = true,
  colorVariants,
  iconMap,
  name,
  testID,
  type = 'primary',
}: Props) => {
  useEffect(requestMaterialSymbolsStylesheet, []);

  const materialIconName = getMaterialIconName(name, iconMap);
  if (!materialIconName) {
    throw new Error(`Invalid icon name: ${name}`);
  }
  const size = name.endsWith('-small') ? 'small' : 'regular';
  const sizePx = calculateIconPxSize(size, allowFontScaling);
  const isDuo = name.endsWith('-duo');
  const color = isDuo ? colorVariants[type].duo : colorVariants[type].main;
  const fill = isDuo ? 1 : 0;

  return (
    <span
      aria-label={accessibilityLabel}
      role="img"
      data-testid={testID}
      className="material-symbols-outlined"
      style={{ color, fontSize: `${sizePx}px`, fontVariationSettings: `'FILL' ${fill}` }}
    >
      {materialIconName}
    </span>
  );
};
